/** Atlas page components — Phase 1. All in one file for brevity. */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import DestinationNotesPanelStub from "@/components/atlas/AtlasComingSoonPage";
// Msg 389 · Slice 1 rebuild — DestinationNotesPanel deleted; legacy
// KB page below stubs the notes block. Real KB lives in AtlasKnowledgeV2.
const DestinationNotesPanel = () => null;
import {
  Sparkles, ShieldCheck, Wallet, BookOpen, PencilRuler, Check, X,
  Loader2, Info,
} from "lucide-react";

const H1 = ({ children }) => (
  <h1 className="text-tb-navy text-3xl md:text-4xl font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>
    {children}
  </h1>
);
const H2 = ({ children }) => (
  <h2 className="text-tb-navy text-xl font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>
    {children}
  </h2>
);
const Page = ({ children, testId }) => (
  <div className="p-6 md:p-8 max-w-6xl" data-testid={testId}>{children}</div>
);
const Card = ({ children, testId, className = "" }) => (
  <div data-testid={testId} className={"rounded-xl bg-white border border-tb-navy/15 p-4 " + className}>
    {children}
  </div>
);

// ── Dashboard ────────────────────────────────────────────────────
export function AtlasDashboardPage() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/atlas/advisor/dashboard").then(r => setD(r.data)); }, []);
  return (
    <Page testId="atlas-dashboard">
      <H1>Welcome back{d?.advisor?.full_name ? `, ${d.advisor.full_name.split(" ")[0]}` : ""}</H1>
      <p className="mt-1 font-hand text-tb-navy/60 text-sm">Your Atlas home base.</p>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card testId="atlas-wallet-card">
          <div className="flex items-center gap-2 text-tb-navy/60 font-hand text-xs uppercase tracking-widest">
            <Wallet className="w-4 h-4" /> Rewards wallet
          </div>
          <div className="mt-2 text-tb-navy text-3xl font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>
            {(d?.wallet?.points ?? 0).toLocaleString()} pts
          </div>
          <div className="text-tb-navy/60 font-hand text-sm">
            ${Number(d?.wallet?.dollars ?? 0).toFixed(2)} cash
          </div>
        </Card>
        <Card testId="atlas-pending-card">
          <div className="flex items-center gap-2 text-tb-navy/60 font-hand text-xs uppercase tracking-widest">
            <PencilRuler className="w-4 h-4" /> Pending submissions
          </div>
          <div className="mt-2 text-tb-navy text-3xl font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>
            {d?.pending_submissions ?? 0}
          </div>
          <div className="text-tb-navy/60 font-hand text-sm">Awaiting admin review</div>
        </Card>
        <Card testId="atlas-quick-links">
          <div className="text-tb-navy/60 font-hand text-xs uppercase tracking-widest">Quick actions</div>
          <div className="mt-2 space-y-1.5">
            <a href="/atlas/knowledge/new" data-testid="atlas-dashboard-cta-builder" className="block px-3 py-2 rounded-lg border border-tb-navy/15 hover:border-tb-navy font-hand text-sm text-tb-navy">
              Build knowledge entry →
            </a>
            <a href="/atlas/knowledge" data-testid="atlas-dashboard-cta-browse" className="block px-3 py-2 rounded-lg border border-tb-navy/15 hover:border-tb-navy font-hand text-sm text-tb-navy">
              Browse knowledge →
            </a>
            <a href="/atlas/search" data-testid="atlas-dashboard-cta-cards" className="block px-3 py-2 rounded-lg border border-tb-navy/15 hover:border-tb-navy font-hand text-sm text-tb-navy">
              Destination cards →
            </a>
          </div>
        </Card>
      </div>
      <H2>Your recent contributions</H2>
      <div className="mt-3">
        {!d?.recent_knowledge?.length ? (
          <p className="font-hand text-tb-navy/55">No approved contributions yet — get started with Knowledge Builder.</p>
        ) : (
          <ul className="space-y-2">
            {d.recent_knowledge.map(k => (
              <li key={k.id} className="p-3 rounded-lg border border-tb-navy/15">
                <div className="text-tb-navy font-medium">{k.title}</div>
                <div className="font-hand text-tb-navy/55 text-xs">{k.kind} · {new Date(k.created_at).toLocaleDateString()} · +{k.wallet_points_awarded} pts</div>
              </li>
            ))}
          </ul>
        )}
      </div>
      {d?.is_atlas_admin && (
        <>
          <H2>Admin quick access</H2>
          <div className="mt-3 flex gap-2">
            <a href="/atlas/admin/review-queue" data-testid="atlas-dashboard-admin-review" className="px-4 h-10 inline-flex items-center rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm">Review queue</a>
            <a href="/atlas/admin/advisors" data-testid="atlas-dashboard-admin-advisors" className="px-4 h-10 inline-flex items-center rounded-full border border-tb-navy/20 text-tb-navy font-hand text-sm">Advisors</a>
          </div>
          <AtlasTaxonomySetupCard />
        </>
      )}
    </Page>
  );
}


// ── Taxonomy setup card (admin-only, dashboard) ─────────────────
// One-click importer for the bundled Atlas taxonomy JSON. Idempotent
// by default — skips anything already present. Meant primarily for
// first-time prod bootstrap when the DB is empty of topics/categories/
// attributes, but safe to re-run in any environment.
function AtlasTaxonomySetupCard() {
  const [counts, setCounts] = useState(null);
  const [loadingCounts, setLoadingCounts] = useState(true);
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const refreshCounts = React.useCallback(async () => {
    setLoadingCounts(true);
    try {
      const { data } = await api.get("/atlas/whoami");
      setCounts(data?.counts || null);
    } catch (e) {
      setCounts(null);
    } finally {
      setLoadingCounts(false);
    }
  }, []);

  useEffect(() => { refreshCounts(); }, [refreshCounts]);

  const runImport = async () => {
    if (running) return;
    setRunning(true);
    setLastResult(null);
    try {
      const { data } = await api.post("/atlas/admin/import-bundled-taxonomy");
      setLastResult(data);
      const ins = data?.inserted || {};
      const skp = data?.skipped || {};
      const totalIns = (ins.topics || 0) + (ins.categories || 0) + (ins.attributes || 0);
      const totalSkp = (skp.topics || 0) + (skp.categories || 0) + (skp.attributes || 0);
      if (totalIns > 0) {
        toast.success(
          `Imported ${totalIns} taxonomy record${totalIns === 1 ? "" : "s"}` +
          (totalSkp ? ` · skipped ${totalSkp} already-present` : "")
        );
      } else {
        toast.success(`Taxonomy already up-to-date — skipped ${totalSkp} record${totalSkp === 1 ? "" : "s"}.`);
      }
      await refreshCounts();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Import failed";
      toast.error(String(msg));
      setLastResult({ error: String(msg) });
    } finally {
      setRunning(false);
    }
  };

  const isEmpty = counts && (counts.atlas_topics === 0 || counts.atlas_categories === 0);

  return (
    <div
      className={
        "mt-8 rounded-xl border p-5 md:p-6 " +
        (isEmpty
          ? "border-tb-gold-bright/70 bg-tb-gold-bright/5"
          : "border-tb-navy/15 bg-white")
      }
      data-testid="atlas-taxonomy-setup-card"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-tb-navy/60 font-hand text-xs uppercase tracking-widest">
            <Sparkles className="w-4 h-4" /> Prod Setup
          </div>
          <div
            className="mt-1 text-tb-navy text-xl font-semibold"
            style={{ fontFamily: '"Playfair Display", serif' }}
          >
            Restore Atlas Taxonomy
          </div>
          <p className="mt-1 font-hand text-tb-navy/60 text-sm max-w-xl">
            Imports the built-in Atlas taxonomy (folders + categories + attributes) into this environment.
            Idempotent — skips anything already present. Safe to run multiple times.
          </p>
        </div>
        <button
          type="button"
          onClick={runImport}
          disabled={running}
          data-testid="atlas-taxonomy-setup-run-btn"
          className={
            "shrink-0 h-11 px-5 inline-flex items-center gap-2 rounded-full font-hand text-sm " +
            (running
              ? "bg-tb-navy/40 text-white cursor-not-allowed"
              : "bg-tb-navy text-tb-gold-bright hover:bg-tb-navy/90")
          }
        >
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookOpen className="w-4 h-4" />}
          {running ? "Importing…" : "Import Taxonomy"}
        </button>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <SetupCountTile
          label="Topics"
          value={loadingCounts ? "…" : counts?.atlas_topics ?? "—"}
          empty={counts?.atlas_topics === 0}
          testId="atlas-taxonomy-count-topics"
        />
        <SetupCountTile
          label="Categories"
          value={loadingCounts ? "…" : counts?.atlas_categories ?? "—"}
          empty={counts?.atlas_categories === 0}
          testId="atlas-taxonomy-count-categories"
        />
        <SetupCountTile
          label="Attributes"
          value={loadingCounts ? "…" : counts?.atlas_attributes ?? "—"}
          empty={counts?.atlas_attributes === 0}
          testId="atlas-taxonomy-count-attributes"
        />
      </div>

      {lastResult && !lastResult.error && (
        <div
          className="mt-4 rounded-lg border border-tb-navy/10 bg-white p-3 text-sm font-hand text-tb-navy/80"
          data-testid="atlas-taxonomy-setup-last-result"
        >
          <div className="flex items-center gap-2 text-tb-navy">
            <Check className="w-4 h-4" /> Last run
            {lastResult.source_exported_at && (
              <span className="text-tb-navy/50 text-xs">
                (seed exported {new Date(lastResult.source_exported_at).toLocaleString()})
              </span>
            )}
          </div>
          <div className="mt-1 grid grid-cols-2 gap-x-6 gap-y-0.5 text-xs">
            <div>Inserted — topics: <b>{lastResult.inserted?.topics ?? 0}</b>, categories: <b>{lastResult.inserted?.categories ?? 0}</b>, attributes: <b>{lastResult.inserted?.attributes ?? 0}</b></div>
            <div>Skipped — topics: <b>{lastResult.skipped?.topics ?? 0}</b>, categories: <b>{lastResult.skipped?.categories ?? 0}</b>, attributes: <b>{lastResult.skipped?.attributes ?? 0}</b></div>
          </div>
          {Array.isArray(lastResult.errors) && lastResult.errors.length > 0 && (
            <div className="mt-2 text-xs text-red-700">
              {lastResult.errors.length} error(s):
              <ul className="list-disc pl-5 mt-0.5">
                {lastResult.errors.slice(0, 5).map((err, i) => <li key={i}>{err}</li>)}
                {lastResult.errors.length > 5 && <li>…and {lastResult.errors.length - 5} more</li>}
              </ul>
            </div>
          )}
        </div>
      )}
      {lastResult?.error && (
        <div
          className="mt-4 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-800 font-hand"
          data-testid="atlas-taxonomy-setup-error"
        >
          <div className="flex items-center gap-2"><X className="w-4 h-4" /> {lastResult.error}</div>
        </div>
      )}
    </div>
  );
}

function SetupCountTile({ label, value, empty, testId }) {
  return (
    <div
      data-testid={testId}
      className={
        "rounded-lg border p-3 " +
        (empty
          ? "border-tb-gold-bright/60 bg-white"
          : "border-tb-navy/15 bg-white")
      }
    >
      <div className="text-[10px] font-hand uppercase tracking-widest text-tb-navy/50">{label}</div>
      <div
        className="mt-0.5 text-tb-navy text-2xl font-semibold"
        style={{ fontFamily: '"Playfair Display", serif' }}
      >
        {value}
      </div>
      {empty && (
        <div className="mt-0.5 text-[11px] font-hand text-tb-navy/50 flex items-center gap-1">
          <Info className="w-3 h-3" /> empty — click Import
        </div>
      )}
    </div>
  );
}


// ── Knowledge browse ────────────────────────────────────────────
export function AtlasKnowledgePage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  useEffect(() => {
    const t = setTimeout(() => {
      api.get(`/atlas/knowledge?q=${encodeURIComponent(q)}&kind=${kind}`).then(r => setItems(r.data.knowledge || []));
    }, 200);
    return () => clearTimeout(t);
  }, [q, kind]);
  return (
    <Page testId="atlas-knowledge">
      <H1>Knowledge base</H1>
      <p className="mt-1 font-hand text-tb-navy/60 text-sm">Approved entries contributed by the advisor community.</p>
      <div className="mt-4 flex gap-2">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search title, body, tags..."
          data-testid="atlas-knowledge-search"
          className="flex-1 h-10 px-3 rounded-full border border-tb-navy/20 focus:border-tb-navy outline-none font-hand text-sm bg-white" />
        <select value={kind} onChange={e => setKind(e.target.value)}
          className="h-10 px-3 rounded-full border border-tb-navy/20 font-hand text-sm bg-white">
          <option value="">All kinds</option>
          <option value="destination">Destination</option>
          <option value="hotel">Hotel</option>
          <option value="supplier">Supplier</option>
          <option value="tip">Tip</option>
          <option value="advisory">Advisory</option>
        </select>
      </div>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.length === 0 && <p className="col-span-full font-hand text-tb-navy/50">Nothing here yet.</p>}
        {items.map(k => (
          <Card key={k.id} testId={`atlas-knowledge-card-${k.id}`}>
            <div className="text-[10px] font-hand uppercase tracking-widest text-tb-navy/50">{k.kind}</div>
            <div className="mt-1 text-tb-navy text-lg font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>{k.title}</div>
            <div className="mt-1 font-hand text-tb-navy/70 text-sm line-clamp-3">{k.body}</div>
            <div className="mt-2 flex flex-wrap gap-1">
              {(k.tags || []).slice(0, 5).map(t => (
                <span key={t} className="text-[10px] font-hand px-2 py-0.5 rounded-full border border-tb-navy/15 text-tb-navy/70">{t}</span>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </Page>
  );
}

// ── Knowledge Builder ───────────────────────────────────────────
export function AtlasKnowledgeBuilderPage() {
  const { user } = useAuth();
  const [kind, setKind] = useState("destination");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [tags, setTags] = useState("");
  const [destinationCode, setDestinationCode] = useState("");
  const [sources, setSources] = useState([]);
  const [researchQuery, setResearchQuery] = useState("");
  const [researching, setResearching] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const runResearch = async () => {
    if (!researchQuery.trim()) return;
    setResearching(true);
    try {
      const { data } = await api.post("/atlas/knowledge/research",
        { query: researchQuery, kind });
      const d = data?.draft || {};
      if (d.title) setTitle(d.title);
      if (d.body) setBody(d.body);
      if (d.tags?.length) setTags(d.tags.join(", "));
      if (d.sources?.length) setSources(d.sources);
      toast.success(`Research complete — ${d.sources?.length || 0} sources gathered.`);
    } catch (e) {
      toast.error("Research failed — try a different query.");
    } finally { setResearching(false); }
  };

  const submit = async () => {
    if (!title.trim() || !body.trim()) { toast.error("Title and body are required."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/atlas/knowledge/submit", {
        kind, title, body,
        tags: tags.split(",").map(t => t.trim()).filter(Boolean),
        sources,
        destination_code: destinationCode.trim().toUpperCase() || null,
      });
      toast.success(`Submitted for review — ${data.wallet_points_pending} pts pending on approval.`);
      setTitle(""); setBody(""); setTags(""); setSources([]); setResearchQuery("");
      setDestinationCode("");
    } catch (e) {
      toast.error("Submit failed.");
    } finally { setSubmitting(false); }
  };

  return (
    <Page testId="atlas-knowledge-builder">
      <H1>Knowledge Builder</H1>
      <p className="mt-1 font-hand text-tb-navy/60 text-sm">Draft a contribution, get admin review, earn wallet points.</p>
      <Card className="mt-6 space-y-4" testId="atlas-kb-form">
        <div className="flex gap-2 items-center">
          <input value={researchQuery} onChange={e => setResearchQuery(e.target.value)}
            data-testid="atlas-kb-research-query"
            placeholder="Research query for Exa (e.g. 'Best restaurants in Lisbon 2026')"
            className="flex-1 h-10 px-3 rounded-full border border-tb-navy/20 outline-none font-hand text-sm bg-white" />
          <button onClick={runResearch} disabled={researching}
            data-testid="atlas-kb-research-btn"
            className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm tb-tap disabled:opacity-50">
            {researching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Research with Exa
          </button>
        </div>
        <div>
          <label className="block font-hand text-xs uppercase tracking-widest text-tb-navy/60 mb-1">Kind</label>
          <select value={kind} onChange={e => setKind(e.target.value)}
            data-testid="atlas-kb-kind"
            className="h-10 px-3 rounded-lg border border-tb-navy/20 font-hand text-sm bg-white">
            <option value="destination">Destination</option>
            <option value="hotel">Hotel</option>
            <option value="supplier">Supplier</option>
            <option value="tip">Tip</option>
            <option value="advisory">Advisory</option>
          </select>
        </div>
        <div>
          <label className="block font-hand text-xs uppercase tracking-widest text-tb-navy/60 mb-1">Title</label>
          <input value={title} onChange={e => setTitle(e.target.value)}
            data-testid="atlas-kb-title"
            className="w-full h-10 px-3 rounded-lg border border-tb-navy/20 font-hand text-sm bg-white" />
        </div>
        <div>
          <label className="block font-hand text-xs uppercase tracking-widest text-tb-navy/60 mb-1">Body (markdown)</label>
          <textarea value={body} onChange={e => setBody(e.target.value)}
            data-testid="atlas-kb-body"
            rows={10}
            className="w-full px-3 py-2 rounded-lg border border-tb-navy/20 font-hand text-sm bg-white" />
          <div className="font-hand text-tb-navy/50 text-xs mt-1">{body.length} chars (200+ earns a quality bonus)</div>
        </div>
        <div>
          <label className="block font-hand text-xs uppercase tracking-widest text-tb-navy/60 mb-1">Tags (comma-separated)</label>
          <input value={tags} onChange={e => setTags(e.target.value)}
            data-testid="atlas-kb-tags"
            className="w-full h-10 px-3 rounded-lg border border-tb-navy/20 font-hand text-sm bg-white" />
        </div>
        {/* Phase 2: linking a Knowledge entry to a destination_code
            makes uploads + notes from either surface (Card OR
            Knowledge) surface bi-directionally on the other. */}
        <div>
          <label className="block font-hand text-xs uppercase tracking-widest text-tb-navy/60 mb-1">Destination code (optional)</label>
          <input value={destinationCode} onChange={e => setDestinationCode(e.target.value.toUpperCase())}
            placeholder="e.g. CUN, LHR, MLE"
            data-testid="atlas-kb-destination-code"
            maxLength={12}
            className="w-full h-10 px-3 rounded-lg border border-tb-navy/20 font-hand text-sm bg-white" />
          <div className="font-hand text-tb-navy/50 text-xs mt-1">
            Link to a Hotelbeds destination code so notes + uploads sync with the matching Destination Card.
          </div>
        </div>
        {sources.length > 0 && (
          <div>
            <div className="font-hand text-xs uppercase tracking-widest text-tb-navy/60 mb-1">Sources ({sources.length})</div>
            <ul className="space-y-1 max-h-40 overflow-y-auto">
              {sources.map((s, i) => (
                <li key={i} className="text-xs font-hand text-tb-navy/75 border-l-2 border-tb-navy/20 pl-2">
                  <a href={s.url} target="_blank" rel="noopener" className="text-tb-navy underline">{s.title || s.url}</a>
                  <div className="text-tb-navy/50 line-clamp-2">{s.snippet}</div>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex justify-end">
          <button onClick={submit} disabled={submitting}
            data-testid="atlas-kb-submit"
            className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-tb-gold-bright text-tb-navy-deep font-hand text-sm tb-tap disabled:opacity-50">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Submit for review
          </button>
        </div>
      </Card>

      {/* Phase 2 · Notes + uploads panel — lets the advisor drop
          screenshots and typed commentary while drafting. If a
          destination_code is set above, contributions surface on the
          matching Destination Card too. */}
      {destinationCode.trim() && (
        <div className="mt-6">
          <DestinationNotesPanel
            sourceSurface="knowledge"
            knowledge={{ id: null, destination_code: destinationCode.trim().toUpperCase() }}
            currentUserId={user?.id}
          />
        </div>
      )}
    </Page>
  );
}

// ── Settings ────────────────────────────────────────────────────
export function AtlasSettingsPage() {
  const [d, setD] = useState(null);
  const [bio, setBio] = useState("");
  const [specialties, setSpecialties] = useState("");
  useEffect(() => {
    api.get("/atlas/advisor/dashboard").then(r => {
      setD(r.data); setBio(r.data.advisor?.bio || "");
      setSpecialties((r.data.advisor?.specialties || []).join(", "));
    });
  }, []);
  const save = async () => {
    await api.patch("/atlas/advisor/profile", {
      bio, specialties: specialties.split(",").map(s => s.trim()).filter(Boolean),
    });
    toast.success("Profile saved.");
  };
  return (
    <Page testId="atlas-settings">
      <H1>Settings</H1>
      <Card className="mt-6 space-y-4">
        <div>
          <label className="block font-hand text-xs uppercase tracking-widest text-tb-navy/60 mb-1">Email</label>
          <input value={d?.advisor?.email || ""} disabled className="w-full h-10 px-3 rounded-lg border border-tb-navy/15 bg-tb-navy/[0.03] font-hand text-sm text-tb-navy/60" />
        </div>
        <div>
          <label className="block font-hand text-xs uppercase tracking-widest text-tb-navy/60 mb-1">Bio</label>
          <textarea value={bio} onChange={e => setBio(e.target.value)} rows={4}
            data-testid="atlas-settings-bio"
            className="w-full px-3 py-2 rounded-lg border border-tb-navy/20 font-hand text-sm bg-white" />
        </div>
        <div>
          <label className="block font-hand text-xs uppercase tracking-widest text-tb-navy/60 mb-1">Specialties (comma-separated)</label>
          <input value={specialties} onChange={e => setSpecialties(e.target.value)}
            data-testid="atlas-settings-specialties"
            className="w-full h-10 px-3 rounded-lg border border-tb-navy/20 font-hand text-sm bg-white" />
        </div>
        <button onClick={save} className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm">Save</button>
      </Card>
    </Page>
  );
}

// ── Wallet placeholder w/ ledger ────────────────────────────────
export function AtlasWalletPage() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/atlas/advisor/wallet").then(r => setD(r.data)); }, []);
  return (
    <Page testId="atlas-wallet">
      <H1>Rewards Wallet</H1>
      <p className="mt-1 font-hand text-tb-navy/60 text-sm">Earning knowledge points already works — full wallet UI coming soon.</p>
      <div className="mt-6 grid grid-cols-2 gap-4 max-w-md">
        <Card><div className="text-tb-navy/60 font-hand text-xs uppercase tracking-widest">Points</div>
          <div className="mt-1 text-tb-navy text-3xl font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>{(d?.balance_points ?? 0).toLocaleString()}</div>
        </Card>
        <Card><div className="text-tb-navy/60 font-hand text-xs uppercase tracking-widest">Cash</div>
          <div className="mt-1 text-tb-navy text-3xl font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>${Number(d?.balance_dollars ?? 0).toFixed(2)}</div>
        </Card>
      </div>
      <div className="mt-6"><h2 className="text-tb-navy text-lg font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>Recent ledger</h2></div>
      <div className="mt-2 rounded-xl border border-tb-navy/15 divide-y divide-tb-navy/10 max-w-3xl">
        {!d?.ledger?.length ? <p className="p-4 font-hand text-tb-navy/50">No wallet events yet.</p> :
          d.ledger.map(e => (
            <div key={e.id} className="p-3 flex items-center gap-3 text-sm font-hand text-tb-navy">
              <span className={"px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest " + (e.delta_points >= 0 ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200")}>
                {e.delta_points >= 0 ? "+" : ""}{e.delta_points} pts
              </span>
              <span className="flex-1 truncate">{e.reason_note}</span>
              <span className="text-tb-navy/50 text-xs">{new Date(e.occurred_at).toLocaleDateString()}</span>
            </div>
          ))
        }
      </div>
    </Page>
  );
}

// ── Coming Soon shared ──────────────────────────────────────────
export function AtlasComingSoon({ title, description, icon: Icon = Info, testId }) {
  return (
    <Page testId={testId}>
      <H1>{title}</H1>
      <div className="mt-8 rounded-xl border border-tb-navy/15 bg-white p-10 text-center max-w-2xl">
        <Icon className="w-10 h-10 mx-auto text-tb-navy/40" strokeWidth={1.5} />
        <div className="mt-3 text-tb-navy text-lg font-semibold" style={{ fontFamily: '"Playfair Display", serif' }}>Coming soon</div>
        <p className="mt-1 font-hand text-tb-navy/60 text-sm max-w-md mx-auto">{description}</p>
      </div>
    </Page>
  );
}

// ── Admin Advisors + Review Queue ───────────────────────────────
export function AtlasAdminAdvisorsPage() {
  const [tab, setTab] = useState("requests");
  const [requests, setRequests] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const load = () => {
    api.get("/atlas/admin/access-requests?status=all").then(r => setRequests(r.data.requests || []));
    api.get("/atlas/admin/advisors").then(r => setAdvisors(r.data.advisors || []));
  };
  useEffect(() => { load(); }, []);
  const approve = async id => { await api.post(`/atlas/admin/access-requests/${id}/approve`); toast.success("Approved"); load(); };
  const deny = async id => { await api.post(`/atlas/admin/access-requests/${id}/deny`, { notes: "" }); toast("Denied"); load(); };
  const deactivate = async uid => { await api.post(`/atlas/admin/advisors/${uid}/deactivate`); toast("Deactivated"); load(); };
  return (
    <Page testId="atlas-admin-advisors">
      <H1>Advisor management</H1>
      <div className="mt-4 flex gap-2">
        <button onClick={() => setTab("requests")} data-testid="atlas-admin-tab-requests"
          className={"px-4 h-9 rounded-full font-hand text-sm " + (tab === "requests" ? "bg-tb-navy text-tb-gold-bright" : "border border-tb-navy/20 text-tb-navy")}>
          Access requests ({requests.filter(r => r.status === "pending").length})
        </button>
        <button onClick={() => setTab("advisors")} data-testid="atlas-admin-tab-advisors"
          className={"px-4 h-9 rounded-full font-hand text-sm " + (tab === "advisors" ? "bg-tb-navy text-tb-gold-bright" : "border border-tb-navy/20 text-tb-navy")}>
          Active advisors ({advisors.length})
        </button>
      </div>
      {tab === "requests" && (
        <div className="mt-4 space-y-2">
          {requests.length === 0 && <p className="font-hand text-tb-navy/50">No access requests.</p>}
          {requests.map(r => (
            <div key={r.id} className="p-3 rounded-lg border border-tb-navy/15 flex items-center gap-3">
              <div className="flex-1">
                <div className="text-tb-navy font-medium">{r.email}</div>
                <div className="font-hand text-tb-navy/55 text-xs">{r.reason || "no reason given"} · {r.status} · {new Date(r.requested_at).toLocaleDateString()}</div>
              </div>
              {r.status === "pending" && (
                <>
                  <button onClick={() => approve(r.id)} data-testid={`atlas-admin-approve-${r.id}`}
                    className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-800 font-hand text-xs">
                    <Check className="w-3 h-3" /> Approve
                  </button>
                  <button onClick={() => deny(r.id)}
                    className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-red-50 border border-red-300 text-red-800 font-hand text-xs">
                    <X className="w-3 h-3" /> Deny
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      {tab === "advisors" && (
        <div className="mt-4 space-y-2">
          {advisors.length === 0 && <p className="font-hand text-tb-navy/50">No active advisors.</p>}
          {advisors.map(a => (
            <div key={a.user_id} className="p-3 rounded-lg border border-tb-navy/15 flex items-center gap-3">
              <div className="flex-1">
                <div className="text-tb-navy font-medium">{a.full_name} — {a.email}</div>
                <div className="font-hand text-tb-navy/55 text-xs">
                  {(a.specialties || []).join(", ") || "no specialties"} · {a.wallet_balance_points || 0} pts
                  {a.deactivated_at && ` · DEACTIVATED ${new Date(a.deactivated_at).toLocaleDateString()}`}
                </div>
              </div>
              {!a.deactivated_at && (
                <button onClick={() => deactivate(a.user_id)}
                  className="inline-flex items-center gap-1 h-8 px-3 rounded-full border border-tb-navy/20 text-tb-navy font-hand text-xs">
                  Deactivate
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </Page>
  );
}

export function AtlasAdminReviewQueuePage() {
  // Msg 391 · Close-out D — three-tab admin queue: facts / promos / notices.
  // K3 (2026-08-05) — added `ingestion` tab surfacing rows routed here
  // from `/atlas/admin/ingest`. Ingestion items carry a `payload` with
  // `upload_id` so admins can jump back to the source document.
  const [tab, setTab] = useState("facts");
  const [facts, setFacts] = useState([]);
  const [promos, setPromos] = useState([]);
  const [notices, setNotices] = useState([]);
  const [ingestions, setIngestions] = useState([]);

  const load = async () => {
    try {
      const [f, p, n, ing] = await Promise.all([
        api.get("/atlas/admin/facts/queue?status=pending_review").catch(() => ({ data: {} })),
        api.get("/atlas/admin/promos/queue?status=pending_review").catch(() => ({ data: {} })),
        api.get("/atlas/admin/operational-notices/queue?status=pending_review").catch(() => ({ data: {} })),
        api.get("/atlas/admin/review-queue?status=pending").catch(() => ({ data: {} })),
      ]);
      setFacts(f.data.facts || []);
      setPromos(p.data.promos || []);
      setNotices(n.data.notices || []);
      setIngestions((ing.data.queue || []).filter((q) => q.kind === "ingestion"));
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const approveFact = async (id) => {
    try {
      const r = await api.post(`/atlas/admin/facts/${id}/approve`);
      toast.success(`Approved — ${r.data.points_awarded || 0} pts credited`);
      load();
    } catch (e) { toast.error(String(e?.response?.data?.detail || e?.message)); }
  };
  const rejectFact = async (id) => {
    try {
      await api.post(`/atlas/admin/facts/${id}/reject`, { reason: "Other", note: "" });
      toast("Rejected"); load();
    } catch (e) { toast.error(String(e?.response?.data?.detail || e?.message)); }
  };
  const approvePromo = async (id) => {
    try {
      const r = await api.post(`/atlas/admin/promos/${id}/approve`);
      toast.success(`Approved — ${r.data.points_awarded || 0} pts credited`);
      load();
    } catch (e) { toast.error(String(e?.response?.data?.detail || e?.message)); }
  };
  const rejectPromo = async (id) => {
    try {
      await api.post(`/atlas/admin/promos/${id}/reject`, { reason: "Other", note: "" });
      toast("Rejected"); load();
    } catch (e) { toast.error(String(e?.response?.data?.detail || e?.message)); }
  };
  const approveNotice = async (id) => {
    try {
      const r = await api.post(`/atlas/admin/operational-notices/${id}/approve`);
      toast.success(`Approved — ${r.data.points_awarded || 0} pts credited`);
      load();
    } catch (e) { toast.error(String(e?.response?.data?.detail || e?.message)); }
  };
  const rejectNotice = async (id) => {
    try {
      await api.post(`/atlas/admin/operational-notices/${id}/reject`, { reason: "Other", note: "" });
      toast("Rejected"); load();
    } catch (e) { toast.error(String(e?.response?.data?.detail || e?.message)); }
  };

  return (
    <Page testId="atlas-admin-review-queue">
      <H1>Knowledge review queue</H1>
      <p className="mt-1 font-hand text-tb-navy/60 text-sm">Approve contributions to publish + credit the advisor's wallet.</p>

      <div className="mt-4 flex items-center gap-1" data-testid="atlas-admin-queue-tabs">
        {[
          { k: "facts",     label: "Facts",     count: facts.length },
          { k: "promos",    label: "Promos",    count: promos.length },
          { k: "notices",   label: "Notices",   count: notices.length },
          { k: "ingestion", label: "Ingestion", count: ingestions.length },
        ].map((t) => (
          <button
            key={t.k}
            type="button"
            onClick={() => setTab(t.k)}
            data-testid={`atlas-admin-queue-tab-${t.k}`}
            className={"h-9 px-4 rounded-full font-hand text-sm " +
              (tab === t.k
                ? "bg-tb-navy text-tb-gold-bright"
                : "border border-tb-navy/20 text-tb-navy/70 hover:bg-tb-navy/5")}
          >
            {t.label} ({t.count})
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-3">
        {tab === "facts" && facts.length === 0 && (
          <p className="font-hand text-tb-navy/50">No pending facts.</p>
        )}
        {tab === "facts" && facts.map((f) => (
          <Card key={f.id} testId={`atlas-admin-fact-${f.id}`}>
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="text-[10px] font-hand uppercase tracking-widest text-tb-navy/50">
                  {f.display_type === "advisor_tip" ? "💡 Advisor Tip" : "🔧 Attribute"} · {f.source?.replace(/_/g, " ")}
                </div>
                <p className="mt-0.5 font-hand text-tb-navy text-sm">{f.text}</p>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {(f.topic_slugs || []).map((s) => (
                    <span key={s} className="text-[10px] font-hand px-2 py-0.5 rounded-full border border-tb-navy/15 text-tb-navy/70">#{s}</span>
                  ))}
                </div>
                <div className="mt-1 font-hand text-[11px] text-tb-navy/50">
                  by {f.contributor_advisor_name || "unknown"}
                </div>
                {f.source_ref && (
                  <a href={f.source_ref} target="_blank" rel="noreferrer" className="mt-1 inline-block font-hand text-[10px] text-tb-navy/60 underline underline-offset-2">
                    View source screenshot
                  </a>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <button onClick={() => approveFact(f.id)} data-testid={`atlas-admin-fact-approve-${f.id}`}
                  className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-800 font-hand text-xs">
                  <Check className="w-3 h-3" /> Approve
                </button>
                <button onClick={() => rejectFact(f.id)} data-testid={`atlas-admin-fact-reject-${f.id}`}
                  className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-red-50 border border-red-300 text-red-800 font-hand text-xs">
                  <X className="w-3 h-3" /> Reject
                </button>
              </div>
            </div>
          </Card>
        ))}

        {tab === "promos" && promos.length === 0 && (
          <p className="font-hand text-tb-navy/50">No pending promos.</p>
        )}
        {tab === "promos" && promos.map((p) => (
          <Card key={p.id} testId={`atlas-admin-promo-${p.id}`}>
            <div className="flex items-start gap-3">
              {p.source_ref && (
                <a href={p.source_ref} target="_blank" rel="noreferrer" className="flex-shrink-0">
                  <img src={p.source_ref} alt="" className="w-16 h-16 object-cover rounded border border-tb-navy/15" />
                </a>
              )}
              <div className="flex-1">
                <div className="text-[10px] font-hand uppercase tracking-widest text-red-800">🎁 Promo · {p.source?.replace(/_/g, " ")}</div>
                <p className="mt-0.5 font-hand text-tb-navy text-sm">{p.text}</p>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {(p.topic_slugs || []).map((s) => (
                    <span key={s} className="text-[10px] font-hand px-2 py-0.5 rounded-full border border-tb-navy/15 text-tb-navy/70">#{s}</span>
                  ))}
                </div>
                <div className="mt-1 font-hand text-[11px] text-tb-navy/60">
                  Book by {p.booking_window_end ? new Date(p.booking_window_end).toLocaleDateString() : (p.expires_at ? new Date(p.expires_at).toLocaleDateString() : "—")}
                  {(p.travel_window_start || p.travel_window_end) && (
                    <> · Sail {p.travel_window_start ? new Date(p.travel_window_start).toLocaleDateString() : "—"}–{p.travel_window_end ? new Date(p.travel_window_end).toLocaleDateString() : "—"}</>
                  )}
                  {" · by "}{p.contributor_advisor_name || "unknown"}
                </div>
                {p.offer_url && (
                  <a href={p.offer_url} target="_blank" rel="noreferrer" className="mt-1 inline-block font-hand text-[10px] text-red-700 underline underline-offset-2">
                    Offer URL: {p.offer_url}
                  </a>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <button onClick={() => approvePromo(p.id)} data-testid={`atlas-admin-promo-approve-${p.id}`}
                  className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-800 font-hand text-xs">
                  <Check className="w-3 h-3" /> Approve
                </button>
                <button onClick={() => rejectPromo(p.id)} data-testid={`atlas-admin-promo-reject-${p.id}`}
                  className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-red-50 border border-red-300 text-red-800 font-hand text-xs">
                  <X className="w-3 h-3" /> Reject
                </button>
              </div>
            </div>
          </Card>
        ))}

        {tab === "notices" && notices.length === 0 && (
          <p className="font-hand text-tb-navy/50">No pending notices.</p>
        )}
        {tab === "notices" && notices.map((n) => (
          <Card key={n.id} testId={`atlas-admin-notice-${n.id}`}>
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="text-[10px] font-hand uppercase tracking-widest text-amber-800">
                  ⚠️ Notice · <span className={n.severity === "critical" ? "text-red-700" : n.severity === "warning" ? "text-amber-800" : "text-blue-700"}>{n.severity || "info"}</span>
                </div>
                <p className="mt-0.5 font-hand text-tb-navy text-sm">{n.text}</p>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {(n.topic_slugs || []).map((s) => (
                    <span key={s} className="text-[10px] font-hand px-2 py-0.5 rounded-full border border-tb-navy/15 text-tb-navy/70">#{s}</span>
                  ))}
                </div>
                <div className="mt-1 font-hand text-[11px] text-tb-navy/60">
                  {n.expires_at ? `Expires ${new Date(n.expires_at).toLocaleDateString()}` : "No expiry"} · by {n.contributor_advisor_name || "unknown"}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <button onClick={() => approveNotice(n.id)} data-testid={`atlas-admin-notice-approve-${n.id}`}
                  className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-800 font-hand text-xs">
                  <Check className="w-3 h-3" /> Approve
                </button>
                <button onClick={() => rejectNotice(n.id)} data-testid={`atlas-admin-notice-reject-${n.id}`}
                  className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-red-50 border border-red-300 text-red-800 font-hand text-xs">
                  <X className="w-3 h-3" /> Reject
                </button>
              </div>
            </div>
          </Card>
        ))}

        {/* K3 (2026-08-05) — ingestion-routed queue rows */}
        {tab === "ingestion" && ingestions.length === 0 && (
          <p className="font-hand text-tb-navy/50">No ingestion items routed to the queue.</p>
        )}
        {tab === "ingestion" && ingestions.map((q) => (
          <Card key={q.id} testId={`atlas-admin-ingestion-${q.id}`}>
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-hand uppercase tracking-widest text-tb-navy/50">
                  Ingestion · {q.payload?.source_document || "document"}
                </div>
                <p className="mt-0.5 font-hand text-tb-navy text-sm">
                  {q.draft?.title}
                </p>
                <p className="mt-1 font-hand text-tb-navy/70 text-xs italic">
                  {(q.draft?.body || "").slice(0, 200)}
                </p>
                <div className="mt-1.5 flex flex-wrap gap-2 font-hand text-[11px] text-tb-navy/60">
                  {q.payload?.proposed_slug && (
                    <span className="px-2 py-0.5 rounded-full border border-tb-navy/15">
                      proposed: #{q.payload.proposed_slug}
                    </span>
                  )}
                  {q.payload?.display_type && (
                    <span className="px-2 py-0.5 rounded-full border border-tb-navy/15">
                      type: {q.payload.display_type}
                    </span>
                  )}
                  {q.submitted_at && (
                    <span>{new Date(q.submitted_at).toLocaleString()}</span>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                {q.payload?.upload_id && (
                  <a
                    href={`/atlas/admin/ingest/${q.payload.upload_id}`}
                    data-testid={`atlas-admin-ingestion-open-${q.id}`}
                    className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs">
                    View source document
                  </a>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </Page>
  );
}
